/**
 * Money is integer minor units everywhere (backbone §0.7; fixtures README).
 * Floats never touch a premium: `0.1 + 0.2` is not 0.3, and an insurer cannot ship that.
 */
export type Cents = number;

export function formatCurrency(cents: Cents, currency = 'EUR', locale = 'de-DE'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

export function formatPercent(value: number, locale = 'de-DE', fractionDigits = 1): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    signDisplay: 'exceptZero',
  }).format(value / 100);
}

/** Summing in integers keeps the aggregate exact — SSOT §4.3 "premium is pure bottom-up aggregation". */
export function sumCents(values: readonly Cents[]): Cents {
  return values.reduce((total, value) => total + value, 0);
}

/**
 * A decimal string from the wire — `"9.33"` — as exact minor units.
 *
 * Every amount on this platform crosses the wire as a DECIMAL STRING, and every amount inside it
 * is integer minor units: a premium is money, and float64 is not (the proto says so on every
 * `Money`). This is the one conversion between the two, and it is done on the DIGITS rather than
 * through a float, because `9.33 * 100` is 932.9999999999999 and `Math.round` over it is a coin
 * flip nobody should be taking with a premium.
 *
 * Returns null for anything that is not a decimal number. A caller that gets null must render the
 * raw string rather than a zero: showing 0,00 € for an amount that failed to parse is the one
 * outcome worse than showing nothing.
 */
export function centsFromDecimal(value: string): Cents | null {
  const trimmed = value.trim();
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(trimmed);
  if (match === null) return null;
  const [, sign = '', whole = '0', fraction = ''] = match;
  // Pad or truncate to exactly two minor digits. Truncation rather than rounding: a third decimal
  // on a premium means the service and this console disagree about the currency's scale, and
  // rounding it away here would hide that instead of showing it.
  const minor = `${fraction}00`.slice(0, 2);
  const cents = Number(`${whole}${minor}`);
  if (!Number.isSafeInteger(cents)) return null;
  return sign === '-' ? -cents : cents;
}

/** An ISO-4217 alphabetic code — what tells a currency amount from a factor or a percentage. */
export const isCurrencyCode = (unit: string): boolean => /^[A-Z]{3}$/.test(unit);
